// Supervisor Command Center — the manager landing page. A "clear cut"
// screen of ACTIONS: To-Do list (flagged calls, coaching alerts, sync
// errors), Quick Actions to push training to staff, and a live pulse.
// Drill-down: alert → Agent Hub (/manager/ops?focus=NAME) → Coaching Lab.
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Command, Loader2, AlertTriangle, PhoneMissed, RefreshCw, Users,
  GraduationCap, Send, MessageSquare, Link2, Activity, ArrowRight, Phone,
  FileSpreadsheet,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEV = {
  high: { dot: 'bg-rose-500', border: 'border-rose-200', label: 'Urgent', chip: 'bg-rose-100 text-rose-700' },
  medium: { dot: 'bg-amber-400', border: 'border-amber-200', label: 'Today', chip: 'bg-amber-100 text-amber-800' },
  low: { dot: 'bg-slate-300', border: 'border-slate-200', label: 'FYI', chip: 'bg-slate-100 text-slate-600' },
};

const KIND_ICON = { flagged_call: PhoneMissed, coaching_alert: AlertTriangle, sync_alert: RefreshCw };

const PulseChip = ({ label, value, tone = 'text-slate-900' }) => (
  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 text-center min-w-[92px]">
    <div className={`text-lg font-bold tabular-nums ${tone}`}>{value ?? '—'}</div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
  </div>
);

const TodoRow = ({ todo, onAgentHub }) => {
  const sev = SEV[todo.severity] || SEV.low;
  const Icon = KIND_ICON[todo.kind] || AlertTriangle;
  return (
    <div className={`flex items-start gap-3 p-3 bg-white border ${sev.border} rounded-lg`} data-testid={`cc-todo-${todo.id}`}>
      <span className={`w-2.5 h-2.5 rounded-full mt-1.5 shrink-0 ${sev.dot}`} />
      <Icon className="w-4 h-4 text-slate-500 mt-1 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-slate-900">{todo.title}</p>
          <span className={`text-[9px] font-bold uppercase rounded-full px-1.5 py-0.5 ${sev.chip}`}>{sev.label}</span>
        </div>
        {todo.detail && <p className="text-xs text-slate-500 mt-0.5">{todo.detail}</p>}
        <div className="flex items-center gap-2 mt-2">
          {(todo.agent_id || todo.agent_name) && (
            <button
              onClick={() => onAgentHub(todo)}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 border border-indigo-200 bg-indigo-50 rounded-lg px-2 py-1 hover:bg-indigo-100"
              data-testid={`cc-agent-hub-${todo.id}`}
            >
              <Users className="w-3 h-3" /> {todo.agent_name || `Ext ${todo.agent_id}`} · {todo.agent_id ? 'Agent Profile' : 'Agent Hub'}
            </button>
          )}
          {todo.link && (
            <Link
              to={todo.link}
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 border border-teal-200 bg-teal-50 rounded-lg px-2 py-1 hover:bg-teal-100"
              data-testid={`cc-open-${todo.id}`}
            >
              {todo.call_id ? <Phone className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
              {todo.call_id ? 'Open in Coaching Lab' : 'Open'}
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

const LINK_PRESETS = [
  { value: '/training', label: 'Training Hub' },
  { value: '/disc-insights', label: 'DISC Insights' },
  { value: '/help', label: 'How Scoring Works' },
  { value: '/knowledge-bases', label: 'Knowledge Bases' },
];

const PushTrainingCard = ({ roster, onPushed }) => {
  const { token } = useAuth();
  const [selected, setSelected] = useState([]);
  const [allAgents, setAllAgents] = useState(true);
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [link, setLink] = useState('/training');
  const [sending, setSending] = useState(false);

  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const send = async () => {
    if (!title.trim()) { toast.error('Give the training push a title'); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/supervisor/push-training`,
        { user_ids: allAgents ? [] : selected, all_agents: allAgents, title, note, link },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Training pushed to ${r.data.pushed} staff member${r.data.pushed === 1 ? '' : 's'}`);
      setTitle(''); setNote('');
      if (onPushed) onPushed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Push failed');
    }
    setSending(false);
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="cc-push-training">
      <p className="text-sm font-bold text-slate-900 flex items-center gap-1.5 mb-1">
        <GraduationCap className="w-4 h-4 text-teal-600" /> Push training to staff
      </p>
      <p className="text-[11px] text-slate-400 mb-3">Lands in each agent&apos;s notification bell with a link to the training screen.</p>
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Title — e.g. Refresh: handling price objections"
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 mb-2"
        data-testid="cc-push-title"
      />
      <textarea
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="Optional note to the team…"
        rows={2}
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 mb-2"
        data-testid="cc-push-note"
      />
      <select
        value={link}
        onChange={(e) => setLink(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 mb-2 bg-white"
        data-testid="cc-push-link"
      >
        {LINK_PRESETS.map((p) => <option key={p.value} value={p.value}>Links to: {p.label}</option>)}
      </select>
      <label className="flex items-center gap-2 text-xs text-slate-700 mb-2">
        <input type="checkbox" checked={allAgents} onChange={(e) => setAllAgents(e.target.checked)} data-testid="cc-push-all" />
        All agents ({roster.length})
      </label>
      {!allAgents && (
        <div className="max-h-32 overflow-y-auto border border-slate-100 rounded-lg p-2 mb-2 space-y-1">
          {roster.map((a) => (
            <label key={a.id} className="flex items-center gap-2 text-xs text-slate-700">
              <input type="checkbox" checked={selected.includes(a.id)} onChange={() => toggle(a.id)} data-testid={`cc-push-agent-${a.id}`} />
              {a.name}{a.extension ? ` (ext ${a.extension})` : ''}
            </label>
          ))}
          {roster.length === 0 && <p className="text-[11px] text-slate-400 italic">No agent accounts in this domain.</p>}
        </div>
      )}
      <button
        onClick={send}
        disabled={sending}
        className="w-full inline-flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-lg px-4 py-2 disabled:opacity-50"
        data-testid="cc-push-send"
      >
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        Push training
      </button>
    </div>
  );
};

// Push receipts — who has opened each training push, with a one-click
// nudge for the agents who haven't.
const RecentPushesCard = ({ refreshKey, onPushed }) => {
  const { token } = useAuth();
  const [pushes, setPushes] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [nudging, setNudging] = useState(null);

  useEffect(() => {
    axios.get(`${API}/supervisor/training-pushes?limit=6`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setPushes(r.data?.pushes || []))
      .catch(() => setPushes([]));
  }, [token, refreshKey]);

  const nudge = async (p) => {
    const unopened = (p.receipts || []).filter((r) => !r.opened).map((r) => r.user_id).filter(Boolean);
    if (!unopened.length) return;
    setNudging(p.id);
    try {
      const r = await axios.post(`${API}/supervisor/push-training`,
        { user_ids: unopened, all_agents: false, title: `Reminder: ${p.title}`, note: p.note || '', link: p.link || '/training' },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Nudged ${r.data.pushed} staff member${r.data.pushed === 1 ? '' : 's'} who hadn't opened it`);
      if (onPushed) onPushed();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Nudge failed');
    }
    setNudging(null);
  };

  if (!pushes.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="cc-push-receipts">
      <p className="text-sm font-bold text-slate-900 mb-1">Push receipts</p>
      <p className="text-[11px] text-slate-400 mb-3">Who has opened each training push — nudge the rest.</p>
      <div className="space-y-2">
        {pushes.map((p) => {
          const pct = p.target_count ? Math.round((p.opened_count / p.target_count) * 100) : 0;
          const isOpen = expanded === p.id;
          return (
            <div key={p.id} className="border border-slate-100 rounded-lg p-2.5" data-testid={`cc-receipt-${p.id}`}>
              <button onClick={() => setExpanded(isOpen ? null : p.id)} className="w-full text-left">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-slate-800 truncate">{p.title}</p>
                  <span className={`text-[11px] font-bold tabular-nums shrink-0 ${pct === 100 ? 'text-emerald-600' : pct > 0 ? 'text-amber-600' : 'text-slate-400'}`} data-testid={`cc-receipt-count-${p.id}`}>
                    {p.opened_count}/{p.target_count} opened
                  </span>
                </div>
                <div className="mt-1.5 bg-slate-100 rounded-full h-1.5 overflow-hidden">
                  <div className={`h-full ${pct === 100 ? 'bg-emerald-500' : 'bg-amber-400'}`} style={{ width: `${Math.max(pct, 2)}%` }} />
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  {p.at ? new Date(p.at).toLocaleString('en-AU', { timeZone: 'Australia/Brisbane', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''} AEST · by {p.by}
                </p>
              </button>
              {isOpen && (
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(p.receipts || []).map((r) => (
                      <span key={r.user_id || r.name} className={`text-[10px] font-semibold rounded-full px-2 py-0.5 ${r.opened ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        {r.opened ? '✓ ' : ''}{r.name}
                      </span>
                    ))}
                  </div>
                  {p.opened_count < p.target_count && (
                    <button
                      onClick={() => nudge(p)}
                      disabled={nudging === p.id}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-800 border border-amber-300 bg-amber-50 rounded-lg px-2.5 py-1 hover:bg-amber-100 disabled:opacity-50"
                      data-testid={`cc-nudge-${p.id}`}
                    >
                      {nudging === p.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      Nudge the {p.target_count - p.opened_count} who haven&apos;t opened
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Classic top-menu tabs (parallel-run phase, Darren Oct 2026): the old
// Dashboard tab bar stays fully accessible from the Command Center. Each
// chip deep-links to /my-domain?tab=<value> — NOTHING legacy is removed.
const CLASSIC_TABS = [
  { value: 'executive', label: 'Executive' },
  { value: 'team-pulse', label: 'Team Pulse' },
  { value: 'team', label: 'Team' },
  { value: 'individual', label: 'Individual' },
  { value: 'intelligence', label: 'Intelligence' },
  { value: 'revivable', label: 'Revivable Opps' },
  { value: 'caller-intel', label: 'Caller Intel' },
  { value: 'my-coaching', label: 'My Coaching' },
  { value: 'coaching', label: 'Coaching Queue' },
  { value: 'gamification', label: 'Achievements' },
  { value: 'reports', label: 'Reports' },
  { value: 'sales-funnel', label: 'Sales Funnel' },
  { value: 'report-builder', label: 'Report Builder' },
];

const ClassicViewsStrip = () => (
  <div className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 mb-5" data-testid="cc-classic-strip">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Classic views — all original tabs</p>
    <div className="flex flex-wrap gap-1.5">
      {CLASSIC_TABS.map((t) => (
        <Link
          key={t.value}
          to={`/my-domain?tab=${t.value}`}
          className="text-[11px] font-semibold text-slate-600 border border-slate-200 bg-slate-50 rounded-lg px-2.5 py-1 hover:bg-teal-50 hover:text-teal-800 hover:border-teal-200"
          data-testid={`cc-classic-${t.value}`}
        >
          {t.label}
        </Link>
      ))}
    </div>
  </div>
);

export default function CommandCenterPage() {
  const { token, uiLayout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [smsUnread, setSmsUnread] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [pushRefresh, setPushRefresh] = useState(0);

  const load = useCallback(async () => {
    try {
      const h = { headers: { Authorization: `Bearer ${token}` } };
      const [cc, sms, chat] = await Promise.all([
        axios.get(`${API}/supervisor/command-center`, h),
        axios.get(`${API}/sms/replies/unread-count`, h).catch(() => ({ data: {} })),
        axios.get(`${API}/livechat/unread-count`, h).catch(() => ({ data: {} })),
      ]);
      setData(cc.data);
      setSmsUnread(sms.data?.count || 0);
      setChatUnread(chat.data?.count || 0);
    } catch {
      setData({ enabled: false });
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);

  const onAgentHub = (todo) => {
    if (todo.agent_id) navigate(`/manager/agent/${encodeURIComponent(todo.agent_id)}`);
    else navigate(`/manager/ops?focus=${encodeURIComponent(todo.agent_name)}`);
  };

  // TF-only parallel run: classic-layout domains bounce to the classic
  // tabbed dashboard instead of the Command Center.
  if (uiLayout === 'classic') return <Navigate to="/my-domain" replace />;

  if (loading || uiLayout === null) {
    return <div className="flex items-center gap-2 text-slate-500 py-20 justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading command center…</div>;
  }
  if (!data?.enabled) {
    return <div className="text-slate-500 py-16 text-center" data-testid="cc-disabled">Command Center is available for supervisors and admins with a domain.</div>;
  }

  const p = data.pulse || {};
  const todos = data.todos || [];

  return (
    <div className="max-w-6xl" data-testid="command-center-page">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Command className="w-6 h-6 text-teal-600" /> Supervisor Command Center
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">Your actions for today — flagged calls, coaching alerts and sync health in one place.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 rounded-lg px-3 py-1.5 hover:bg-slate-50" data-testid="cc-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </div>

      <ClassicViewsStrip />

      {/* Live pulse */}
      <div className="flex gap-2 flex-wrap mb-5" data-testid="cc-pulse">
        <PulseChip label="Graded today" value={p.graded_today} />
        <PulseChip label="Avg score 7d" value={p.avg_score_7d} />
        <PulseChip label="Open revivables" value={p.open_revivables} tone={p.open_revivables > 0 ? 'text-amber-600' : 'text-slate-900'} />
        {p.gymsales_enabled && (
          <PulseChip label="CRM match 7d" value={p.match_rate_7d != null ? `${p.match_rate_7d}%` : '—'}
            tone={p.match_rate_7d != null && p.match_rate_7d < 50 ? 'text-rose-600' : 'text-emerald-600'} />
        )}
        <PulseChip label="SMS unread" value={smsUnread} tone={smsUnread > 0 ? 'text-teal-600' : 'text-slate-900'} />
        <PulseChip label="Chats waiting" value={chatUnread} tone={chatUnread > 0 ? 'text-teal-600' : 'text-slate-900'} />
        <div className="bg-white border border-slate-200 rounded-lg px-3 py-2 flex items-center gap-2" data-testid="cc-sync-status">
          <Activity className={`w-4 h-4 ${p.sync_status === 'active' ? 'text-emerald-500' : 'text-amber-500'}`} />
          <div>
            <div className="text-xs font-semibold text-slate-800 capitalize">{p.sync_status || 'unknown'}</div>
            <div className="text-[10px] text-slate-400">call sync</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* To-Do list */}
        <div className="lg:col-span-2">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Supervisor to-do ({todos.length})</p>
          <div className="space-y-2" data-testid="cc-todo-list">
            {todos.length === 0 && (
              <div className="bg-white border border-emerald-200 rounded-lg p-6 text-center text-emerald-700 text-sm" data-testid="cc-todo-empty">
                All clear — no flagged calls, coaching alerts or sync issues right now.
              </div>
            )}
            {todos.map((t) => <TodoRow key={t.id} todo={t} onAgentHub={onAgentHub} />)}
          </div>
        </div>

        {/* Quick actions */}
        <div className="space-y-4">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Quick actions</p>
          <PushTrainingCard roster={data.roster || []} onPushed={() => setPushRefresh((k) => k + 1)} />
          <RecentPushesCard refreshKey={pushRefresh} onPushed={() => setPushRefresh((k) => k + 1)} />
          <div className="grid grid-cols-2 gap-2">
            <Link to="/manager/ops" className="bg-white border border-slate-200 rounded-lg p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="cc-qa-agent-hub">
              <Users className="w-4 h-4 text-indigo-600" /> Agent Hub
            </Link>
            <Link to="/gymsales-sync" className="bg-white border border-slate-200 rounded-lg p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="cc-qa-sync">
              <Link2 className="w-4 h-4 text-teal-600" /> GymSales Sync
            </Link>
            <Link to="/sms-inbox" className="bg-white border border-slate-200 rounded-lg p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="cc-qa-sms">
              <MessageSquare className="w-4 h-4 text-emerald-600" /> SMS Inbox
            </Link>
            <Link to="/calls" className="bg-white border border-slate-200 rounded-lg p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="cc-qa-calls">
              <Phone className="w-4 h-4 text-slate-600" /> Coaching Lab
            </Link>
            <Link to="/my-domain?tab=report-builder" className="bg-white border border-slate-200 rounded-lg p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="cc-qa-report-builder">
              <FileSpreadsheet className="w-4 h-4 text-violet-600" /> Report Builder
            </Link>
            <Link to="/my-domain?tab=revivable" className="bg-white border border-slate-200 rounded-lg p-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2" data-testid="cc-qa-revivable">
              <RefreshCw className="w-4 h-4 text-amber-600" /> Revivable Opps
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
